<template>
  <div class="communities-page container">
    <div class="loading-completed">
      <TheBreadcrumb :pages="arianeRoute" />
      <HeaderPage :page="currentPageInfo" />
      <section
        v-if="userCommunities.length && authenticated"
        class="my-communities section"
      >
        <div class="title-line">
          <h2 class="title-strat">Communautés que vous suivez</h2>
        </div>
        <div class="slider">
          <CommunitySlider
            :communities="userCommunities"
            :animated="false"
          />
        </div>
      </section>
      <section
        v-if="universes.length"
        class="game-universes section"
      >
        <div class="title-line line">
          <h2 class="title-strat">Les univers</h2>
        </div>
        <UniversesSlider :universes="universes" />
      </section>
      <section class="other-communities section">
        <div class="title-line">
          <h2
            v-if="userCommunities.length"
            class="title-strat"
          >
            Les autres communautés
          </h2>
          <h2
            v-else
            class="title-strat"
          >
            Les communautés
          </h2>
          <div class="search-field">
            <input
              v-model="search"
              name="community-search"
              class="search-input"
              placeholder="Rechercher une communauté"
              @keyup.enter="searchData"
            />
            <img
              v-show="search"
              src="~assets/icons/close.svg"
              alt="Effacer la recherche"
              class="search-clear"
              @click="clearSearch"
            />
            <div
              class="search-button"
              @click="searchData"
            >
              <img
                src="~assets/icons/search.svg"
                alt="Rechercher"
              />
            </div>
          </div>
        </div>

        <div
          v-if="communities.length == 0"
          class="no-result"
        >
          <h2 class="title-strat">{{ noContent }}</h2>
        </div>
        <div
          v-if="gameCommunities.length"
          class="game-section sub-section"
        >
          <h5>Jeux</h5>
          <div
            :key="sliderGameKey"
            class="slider"
          >
            <CommunitySlider
              :communities="gameCommunities"
              :initial-slide="currentPositionGame"
              :animated="animateGame"
              @change-slide="changeForGame"
            />
          </div>
        </div>
        <div
          v-if="consoleCommunities.length"
          class="console-section sub-section"
        >
          <h5>Consoles et Accessoires</h5>
          <div
            :key="sliderConsoleKey"
            class="slider"
          >
            <CommunitySlider
              :communities="consoleCommunities"
              :initial-slide="currentPositionConsole"
              :animated="animateConsole"
              @change-slide="changeForConsole"
            />
          </div>
        </div>
        <div
          v-if="serviceCommunities.length"
          class="service-section sub-section"
        >
          <h5>Applications / Services</h5>
          <div
            :key="sliderServiceKey"
            class="slider"
          >
            <CommunitySlider
              :communities="serviceCommunities"
              :initial-slide="currentPositionService"
              :animated="animateService"
              @change-slide="changeForService"
            />
          </div>
        </div>
        <div
          v-if="freeCommunities.length"
          class="free-section sub-section"
        >
          <h5>Libres</h5>
          <div
            :key="sliderFreeKey"
            class="slider"
          >
            <CommunitySlider
              :communities="freeCommunities"
              :initial-slide="currentPositionFree"
              :animated="animateFree"
              @change-slide="changeForFree"
            />
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  middleware: () => {
    const loadCommunities = useLoadCommunities()
    return loadCommunities()
  },
})

const universesStore = useUniversesStore()
const { universes } = storeToRefs(universesStore)

const communitiesStore = useCommunitiesStore()
const {
  communities,
  userCommunities,
  communitiesPagesGame,
  communitiesPagesConsole,
  communitiesPagesService,
  communitiesPagesFree,
} = storeToRefs(communitiesStore)

const arianeRoute = [
  {
    title: 'Toutes les communautés',
    route: '',
  },
]

const breadcrumbSchema = useBreadcrumbSchema({
  arianeRoute,
})

useHead({
  title: 'Tests, avis, astuces et solutions Jeux Videos PS5, PS4',
  script: [breadcrumbSchema],
})

useSeoMeta({
  title: 'Toutes les communautés We Are PlayStation',
  description:
    'Découvrez toutes les communautés de jeux vidéo, consoles et services sur PlayStation.',
  ogTitle: 'Toutes les communautés We Are PlayStation',
  ogDescription:
    'Découvrez toutes les communautés de jeux vidéo, consoles et services sur PlayStation.',
  ogImage: 'https://www.weareplaystation.fr/img/toutes-les-communautes.jpg',
})

const noContent = ref('Aucun contenu')
const search = ref('')
const sliderGameKey = ref(0)
const sliderServiceKey = ref(1000)
const sliderConsoleKey = ref(2000)
const sliderFreeKey = ref(3000)
const currentPageGame = ref(1)
const currentPageConsole = ref(1)
const currentPageService = ref(1)
const currentPageFree = ref(1)
const currentPositionGame = ref(0)
const currentPositionService = ref(0)
const currentPositionConsole = ref(0)
const currentPositionFree = ref(0)
const tempPositionGame = ref(0)
const tempPositionService = ref(0)
const tempPositionConsole = ref(0)
const tempPositionFree = ref(0)
const communitiesPerPage = ref(16)
const animateGame = ref(true)
const animateConsole = ref(true)
const animateService = ref(true)
const animateFree = ref(true)

const currentPageInfo = {
  title: 'toutes les communautés',
  image: '/img/toutes-les-communautes.jpg',
  titleSmall: 'TOUTES LES',
  titleBig: 'COMMUNAUTÉS',
  description:
    'Découvrez toutes les communautés de jeux vidéo, consoles et services sur PlayStation.',
}

const { authenticated } = useAuthenticated()

const gameCommunities = computed(() => {
  return communities.value.filter((community) => community.type?.slug === 'jeu')
})

const consoleCommunities = computed(() => {
  return communities.value.filter(
    (community) => community.type?.slug === 'console',
  )
})

const freeCommunities = computed(() => {
  return communities.value.filter(
    (community) => community.type?.slug === 'libre',
  )
})

const serviceCommunities = computed(() => {
  return communities.value.filter(
    (community) => community.type?.slug === 'service',
  )
})

const changeForGame = async (value: number) => {
  tempPositionGame.value = value

  if (value > communitiesPerPage.value * currentPageGame.value - 10) {
    if (currentPageGame.value < communitiesPagesGame.value) {
      animateGame.value = false
      currentPageGame.value = currentPageGame.value + 1

      if (authenticated.value) {
        await communitiesStore.fetchUserNotCommunitiesType({
          page: currentPageGame.value,
          type: 'jeu',
        })
      } else {
        await communitiesStore.fetchCommunitiesType({
          page: currentPageGame.value,
          type: 'jeu',
          search: '',
        })
      }

      currentPositionGame.value = tempPositionGame.value
      sliderGameKey.value++
    }
  }
}

const changeForService = async (value: number) => {
  tempPositionService.value = value

  if (value > communitiesPerPage.value * currentPageService.value - 10) {
    if (currentPageService.value < communitiesPagesService.value) {
      animateService.value = false
      currentPageService.value = currentPageService.value + 1
      if (authenticated.value) {
        await communitiesStore.fetchUserNotCommunitiesType({
          page: currentPageService.value,
          type: 'service',
        })
      } else {
        await communitiesStore.fetchCommunitiesType({
          page: currentPageService.value,
          type: 'service',
          search: '',
        })
      }

      currentPositionService.value = tempPositionService.value
      sliderServiceKey.value++
    }
  }
}

const changeForConsole = async (value: number) => {
  tempPositionConsole.value = value

  if (value > communitiesPerPage.value * currentPageConsole.value - 10) {
    if (currentPageConsole.value < communitiesPagesConsole.value) {
      animateConsole.value = false
      currentPageConsole.value = currentPageConsole.value + 1
      if (authenticated.value) {
        await communitiesStore.fetchUserNotCommunitiesType({
          page: currentPageConsole.value,
          type: 'console',
        })
      } else {
        await communitiesStore.fetchCommunitiesType({
          page: currentPageConsole.value,
          type: 'console',
          search: '',
        })
      }

      currentPositionConsole.value = tempPositionConsole.value
      sliderConsoleKey.value++
    }
  }
}

const changeForFree = async (value: number) => {
  tempPositionFree.value = value

  if (value > communitiesPerPage.value * currentPageFree.value - 10) {
    if (currentPageFree.value < communitiesPagesFree.value) {
      animateFree.value = false
      currentPageFree.value = currentPageFree.value + 1
      if (authenticated.value) {
        await communitiesStore.fetchUserNotCommunitiesType({
          page: currentPageFree.value,
          type: 'libre',
        })
      } else {
        await communitiesStore.fetchCommunitiesType({
          page: currentPageFree.value,
          type: 'libre',
          search: '',
        })
      }

      currentPositionFree.value = tempPositionFree.value
      sliderFreeKey.value++
    }
  }
}

const searchData = async () => {
  if (search.value === '') {
    if (authenticated.value) {
      await communitiesStore.fetchUserNotCommunities()
    } else {
      await communitiesStore.fetchCommunities()
    }
  } else {
    await communitiesStore.fetchCommunities({
      search: search.value.toLowerCase(),
    })
  }

  currentPositionGame.value = 0
  currentPositionConsole.value = 0
  currentPositionService.value = 0
  currentPositionFree.value = 0

  currentPageGame.value = 1
  currentPageConsole.value = 1
  currentPageService.value = 1
  currentPageFree.value = 1

  if (!communities.value.length) {
    if (search.value === '') {
      noContent.value = 'Aucun contenu'
    } else {
      noContent.value = 'Aucun résultat pour votre recherche'
    }
  }
}

const loadCommunities = useLoadCommunities()

watch(authenticated, (newValue) => {
  if (newValue) {
    loadCommunities()
  }
})

const clearSearch = async () => {
  search.value = ''
  await searchData()
}
</script>

<style lang="scss" scoped>
.communities-page {
  min-height: 100vh;
  padding-bottom: 10rem;

  .title-line {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .search-field {
    position: relative;
    width: 34.5rem;

    .search-input {
      display: block;
      width: 100%;
      padding: 10px;
      padding-left: 0 !important;
      color: #000;
      background: #fff;
      border: 0 !important;
      border-bottom: 1px solid rgb(0 0 0 / 0.2) !important;
      border-radius: 0;
      outline: none;
      transition: all 0.1s;
      transition-delay: 0.05s;
    }

    .search-clear {
      position: absolute;
      top: 50%;
      right: 3rem;
      width: 1.4rem;
      height: 1.4rem;
      cursor: pointer;
      transform: translateY(-50%);
    }

    .search-button {
      position: absolute;
      top: 50%;
      right: 0;
      cursor: pointer;
      transform: translateY(-50%);

      img {
        width: 1.67rem;
        height: 1.67rem;
        object-fit: contain;
      }
    }
  }

  .pagination {
    display: flex;
    justify-content: center;
    margin-top: 7rem;
    list-style: none;

    li {
      padding: 0 2rem 0 0;
      font-size: 1.4rem;
      font-weight: 500;
      line-height: 3.4rem;
      cursor: pointer;
      opacity: 0.4;

      &::before {
        content: '-';
        margin-right: 2rem;
        font-size: 1.4rem;
        font-weight: 500;
        line-height: 3.4rem;
      }

      &:first-child {
        &::before {
          content: '';
        }
      }

      &.current {
        opacity: 1;

        &::before {
          opacity: 0.4;
        }
      }
    }
  }

  .sub-section {
    margin-top: 5.5rem;

    h5 {
      margin-bottom: 4rem;
      font-size: 1.6rem;
      font-weight: 400;
      line-height: 2.1rem;
    }
  }
}

@media screen and (max-width: 700px) {
  .communities-page {
    .section {
      padding-right: 0;
      padding-left: 0;
    }

    .sub-section {
      h5 {
        padding-left: 2.5rem;
      }
    }
  }
}

@media screen and (max-width: 430px) {
  .communities-page {
    .other-communities {
      .title-line {
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;

        .search-field {
          margin-top: 1rem;
          padding-right: 2.5rem;

          .search-button {
            right: 2.5rem;
          }
        }
      }
    }
  }
}
</style>
